import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { getTabScrollVisibility } from '../utils/tab-scroll.util';

/**
 * SettingsComponent provides a responsive, tabbed settings UI that displays and manages available settings sections based on the current user's role, and provides smooth horizontal scrolling of the tab menu when necessary.
 * @component
 * @selector app-settings
 * @example
 *   <app-settings [userProfile]="currentUser" [activeTab]="'User Profile'" (tabChange)="onTabChange($event)"></app-settings>
 * @input {{ { role: string } | null }} userProfile - The current user's profile object (or null). Determines which tabs are visible (admin sees all tabs; staff sees a reduced set).
 * @input {string} activeTab - The name of the currently active tab. Defaults to 'User Profile'.
 * @output {EventEmitter<string>} tabChange - Emits the name of the newly selected tab when the active tab changes.
 *
 * Key properties:
 * - tabMenu (ViewChild, ElementRef): Reference to the horizontal tab menu element used for measuring and scrolling.
 * - tabContainer (ViewChild, ElementRef): Reference to the container wrapping the tabs (used for layout/measurements).
 * - showLeftScroll {boolean}: Indicates whether the left scroll control should be shown.
 * - showRightScroll {boolean}: Indicates whether the right scroll control should be shown.
 *
 * Key methods:
 * - get tabs(): Returns the list of tabs to render based on userProfile.role. Admin sees all tabs; staff sees only 'User Profile' and 'Help & Support'; others see none.
 * - checkScroll(): Recalculates visibility of horizontal scroll controls for the tab menu (sets showLeftScroll/showRightScroll). Runs asynchronously to read layout after rendering.
 * - setActiveTab(tabName: string): Sets the active tab, emits tabChange, and updates scroll controls.
 * - scrollLeft(): Smoothly scrolls the tab menu left and updates scroll control visibility.
 * - scrollRight(): Smoothly scrolls the tab menu right and updates scroll control visibility.
 *
 * Important lifecycle hooks and listeners:
 * - ngAfterViewInit(): After view init, calls checkScroll() to initialize scroll control states.
 * - @HostListener('window:resize', ['$event']) onResize(): Recomputes scroll control visibility on window resize to remain responsive.
 */
@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements AfterViewInit {
  @Input() userProfile: { role: string } | null = null;
  @Input() activeTab = 'User Profile';
  @Output() tabChange = new EventEmitter<string>();

  @ViewChild('tabContainer') tabContainer!: ElementRef;
  @ViewChild('tabMenu') tabMenu!: ElementRef;

  showLeftScroll = false;
  showRightScroll = false;

  /**
   * Return the list of settings tabs appropriate for the current user's role.
   * @example
   * tabs()
   * [{ name: 'User Profile', icon: 'assets/images/settings-icons/user-cog.svg' }, ...]
   * @param {Object} userProfile - The current user's profile object (accessed via this.userProfile) used to determine available tabs.
   * @returns {Array<Object>} Array of tab objects ({ name: string, icon: string }) to display in the settings UI.
   **/
  get tabs() {
    const allTabs = [
      {
        name: 'User Profile',
        icon: 'assets/images/settings-icons/user-cog.svg',
      },
      {
        name: 'Account Settings',
        icon: 'assets/images/settings-icons/adjustments.svg',
      },
      {
        name: 'Business Profile',
        icon: 'assets/images/settings-icons/briefcase.svg',
      },
      {
        name: 'Location Management',
        icon: 'assets/images/settings-icons/map-pin-cog.svg',
      },
      {
        name: 'Payment',
        icon: 'assets/images/settings-icons/credit-card.svg',
      },
      {
        name: 'Terminal',
        icon: 'assets/images/settings-icons/terminal.svg',
      },
      {
        name: 'Help & Support',
        icon: 'assets/images/settings-icons/headset.svg',
      },
    ];

    if (this.userProfile?.role === 'admin') {
      return allTabs;
    }
    // For staff users, only show User Profile and Help & Support
    if (
      this.userProfile?.role === 'attendant' ||
      this.userProfile?.role === 'attendant_plus' ||
      this.userProfile?.role === 'staff'
    ) {
      return allTabs.filter(tab => tab.name === 'User Profile' || tab.name === 'Help & Support');
    }
    return [];
  }

  ngAfterViewInit() {
    this.checkScroll();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScroll();
  }

  checkScroll() {
    setTimeout(() => {
      const menu = this.tabMenu?.nativeElement;
      if (!menu) return;
      const { showLeftScroll, showRightScroll } = getTabScrollVisibility(menu);
      this.showLeftScroll = showLeftScroll;
      this.showRightScroll = showRightScroll;
    }, 0);
  }

  setActiveTab(tabName: string) {
    this.activeTab = tabName;
    this.tabChange.emit(tabName);
    this.checkScroll();
  }

  scrollLeft() {
    this.tabMenu.nativeElement.scrollBy({ left: -200, behavior: 'smooth' });
    this.checkScroll();
  }

  scrollRight() {
    this.tabMenu.nativeElement.scrollBy({ left: 200, behavior: 'smooth' });
    this.checkScroll();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
